import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import Layout from './components/layout/Layout';
import Dashboard from './pages/Dashboard';
import Properties from './pages/Properties';
import PropertyDetail from './pages/PropertyDetail';
import Alerts from './pages/Alerts';
import Scrapers from './pages/Scrapers';
import AdSubmit from './pages/AdSubmit';
import AdminAds from './pages/AdminAds';
import AdminUsers from './pages/AdminUsers';
import AdminPromoCodes from './pages/AdminPromoCodes';
import AdminLoginLog from './pages/AdminLoginLog';
import UploadListings from './pages/UploadListings';
import Features from './pages/Features';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { WatchlistProvider } from './contexts/WatchlistContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import Login from './pages/Login';
import Register from './pages/Register';
import ChoosePlan from './pages/ChoosePlan';
import Account from './pages/Account';
import ContactSupport from './pages/ContactSupport';
import Neighbourhood from './pages/Neighbourhood';
import MyProperties from './pages/MyProperties';
import BrokerShared from './pages/BrokerShared';
import IdentityVault from './pages/IdentityVault';
import AdminDocumentAccessLog from './pages/AdminDocumentAccessLog';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import RootRoute from './components/auth/RootRoute';
import AdminOps from './pages/AdminOps';
import PropertyAnalysis from './pages/PropertyAnalysis';

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  // is_superuser is the platform-admin flag — layered on top of any role.
  if (!user.is_superuser) return <Navigate to="/dashboard" replace />;
  return children;
}

function AppShell() {
  const { user } = useAuth();
  return (
    <WatchlistProvider isLoggedIn={!!user}>
      <Toaster
        position="top-right"
        toastOptions={{
          style: { background: '#1e293b', color: '#e2e8f0', border: '1px solid #334155' },
        }}
      />
      <Routes>
        <Route path="/" element={<RootRoute />} />
        <Route path="/" element={<Layout />}>
          <Route path="dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="properties" element={<ProtectedRoute><Properties /></ProtectedRoute>} />
          <Route path="properties/:id" element={<ProtectedRoute><PropertyDetail /></ProtectedRoute>} />
          <Route path="analysis" element={<ProtectedRoute><PropertyAnalysis /></ProtectedRoute>} />
          <Route path="neighbourhood" element={<ProtectedRoute><Neighbourhood /></ProtectedRoute>} />
          <Route path="neighbourhood/:postcode" element={<ProtectedRoute><Neighbourhood /></ProtectedRoute>} />
          <Route path="alerts" element={<ProtectedRoute><Alerts /></ProtectedRoute>} />
          <Route path="my-properties" element={<ProtectedRoute><MyProperties /></ProtectedRoute>} />
          <Route path="broker/shared" element={<ProtectedRoute><BrokerShared /></ProtectedRoute>} />
          <Route path="upload" element={<ProtectedRoute><UploadListings /></ProtectedRoute>} />
          <Route path="account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
          <Route path="account/identity" element={<ProtectedRoute><IdentityVault /></ProtectedRoute>} />
          <Route path="support" element={<ProtectedRoute><ContactSupport /></ProtectedRoute>} />
          <Route path="admin/document-access-log" element={<AdminRoute><AdminDocumentAccessLog /></AdminRoute>} />
          <Route path="scrapers" element={<AdminRoute><Scrapers /></AdminRoute>} />
          <Route path="admin/ads" element={<AdminRoute><AdminAds /></AdminRoute>} />
          <Route path="admin/users" element={<AdminRoute><AdminUsers /></AdminRoute>} />
          <Route path="admin/promo-codes" element={<AdminRoute><AdminPromoCodes /></AdminRoute>} />
          <Route path="admin/login-log" element={<AdminRoute><AdminLoginLog /></AdminRoute>} />
        </Route>
        <Route path="/features" element={<Features />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/choose-plan" element={<ChoosePlan />} />
        <Route path="/advertise" element={<AdSubmit />} />
        <Route path="/admin" element={<AdminOps />} />
      </Routes>
    </WatchlistProvider>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppShell />
      </AuthProvider>
    </BrowserRouter>
  );
}
